// Lead grouping for the table's Region/Län column, the map sidebar, and the
// dashboard. Swedish leads are grouped by län (county). Län borders are
// irregular and can't be derived from lat/lng, so each city is tagged with its
// län explicitly in CITY_LAN below (keyed by the NORMALIZED city name, matching
// cityCoords.ts). Cities not in the table fall back to "Okänt län"; non-SE
// countries keep coarse coordinate regions (used by the all-country map only).

export interface Region {
  key: string
  label: string
  order: number
}

export const UNKNOWN_REGION: Region = { key: 'unknown', label: 'Okänt län', order: 99 }
export const OTHER_REGION: Region = { key: 'other', label: 'Utlandet', order: 98 }

// The 21 Swedish län, ordered roughly north → south so the map sidebar / filter
// reads top-to-bottom the way the old coarse regions did.
interface LanMeta { label: string; order: number }
const LAN: Record<string, LanMeta> = {
  'se-norrbotten':     { label: 'Norrbottens län',     order: 0 },
  'se-vasterbotten':   { label: 'Västerbottens län',   order: 1 },
  'se-jamtland':       { label: 'Jämtlands län',       order: 2 },
  'se-vasternorrland': { label: 'Västernorrlands län', order: 3 },
  'se-gavleborg':      { label: 'Gävleborgs län',      order: 4 },
  'se-dalarna':        { label: 'Dalarnas län',        order: 5 },
  'se-varmland':       { label: 'Värmlands län',       order: 6 },
  'se-vastmanland':    { label: 'Västmanlands län',    order: 7 },
  'se-uppsala':        { label: 'Uppsala län',         order: 8 },
  'se-stockholm':      { label: 'Stockholms län',      order: 9 },
  'se-sodermanland':   { label: 'Södermanlands län',   order: 10 },
  'se-orebro':         { label: 'Örebro län',          order: 11 },
  'se-ostergotland':   { label: 'Östergötlands län',   order: 12 },
  'se-vastra-gotaland':{ label: 'Västra Götalands län', order: 13 },
  'se-jonkoping':      { label: 'Jönköpings län',      order: 14 },
  'se-kronoberg':      { label: 'Kronobergs län',      order: 15 },
  'se-kalmar':         { label: 'Kalmar län',          order: 16 },
  'se-gotland':        { label: 'Gotlands län',        order: 17 },
  'se-halland':        { label: 'Hallands län',        order: 18 },
  'se-blekinge':       { label: 'Blekinge län',        order: 19 },
  'se-skane':          { label: 'Skåne län',           order: 20 },
}

// City (normalized) → län key. Covers the cities in cityCoords.ts. Extend this
// alongside cityCoords when adding new localities.
const CITY_LAN: Record<string, string> = {
  // Stockholms län
  STOCKHOLM: 'se-stockholm', KISTA: 'se-stockholm', SOLNA: 'se-stockholm',
  SOLLENTUNA: 'se-stockholm', TÄBY: 'se-stockholm', DANDERYD: 'se-stockholm',
  BROMMA: 'se-stockholm', HÄGERSTEN: 'se-stockholm', ÅRSTA: 'se-stockholm',
  JOHANNESHOV: 'se-stockholm', 'STOCKHOLM-GLOBEN': 'se-stockholm',
  ENSKEDE: 'se-stockholm', SKARPNÄCK: 'se-stockholm',
  'UPPLANDS VÄSBY': 'se-stockholm', ROSERSBERG: 'se-stockholm',
  KUNGSÄNGEN: 'se-stockholm', SÖDERTÄLJE: 'se-stockholm', NACKA: 'se-stockholm',
  LIDINGÖ: 'se-stockholm', SUNDBYBERG: 'se-stockholm', HUDDINGE: 'se-stockholm',
  JÄRFÄLLA: 'se-stockholm', ÅKERSBERGA: 'se-stockholm', VALLENTUNA: 'se-stockholm',
  TYRESÖ: 'se-stockholm', HANINGE: 'se-stockholm', MÄRSTA: 'se-stockholm',
  SIGTUNA: 'se-stockholm',

  // Västra Götalands län
  GÖTEBORG: 'se-vastra-gotaland', MÖLNDAL: 'se-vastra-gotaland',
  'VÄSTRA FRÖLUNDA': 'se-vastra-gotaland', MÖLNLYCKE: 'se-vastra-gotaland',
  PARTILLE: 'se-vastra-gotaland', KUNGÄLV: 'se-vastra-gotaland',
  LERUM: 'se-vastra-gotaland', ALINGSÅS: 'se-vastra-gotaland',
  UDDEVALLA: 'se-vastra-gotaland', TROLLHÄTTAN: 'se-vastra-gotaland',
  VÄNERSBORG: 'se-vastra-gotaland', BORÅS: 'se-vastra-gotaland',
  SKÖVDE: 'se-vastra-gotaland', LIDKÖPING: 'se-vastra-gotaland',

  // Hallands län
  KUNGSBACKA: 'se-halland', VARBERG: 'se-halland', HALMSTAD: 'se-halland',

  // Skåne län
  MALMÖ: 'se-skane', LUND: 'se-skane', HELSINGBORG: 'se-skane',
  ÄNGELHOLM: 'se-skane', YSTAD: 'se-skane', STAFFANSTORP: 'se-skane',
  ESLÖV: 'se-skane', HÄSSLEHOLM: 'se-skane', KRISTIANSTAD: 'se-skane',
  TRELLEBORG: 'se-skane', LANDSKRONA: 'se-skane',

  // Uppsala län
  UPPSALA: 'se-uppsala', ENKÖPING: 'se-uppsala',

  // Västmanlands län
  VÄSTERÅS: 'se-vastmanland',

  // Södermanlands län
  ESKILSTUNA: 'se-sodermanland', NYKÖPING: 'se-sodermanland',

  // Östergötlands län
  LINKÖPING: 'se-ostergotland', NORRKÖPING: 'se-ostergotland',
  MOTALA: 'se-ostergotland', RIMFORSA: 'se-ostergotland',

  // Jönköpings län
  JÖNKÖPING: 'se-jonkoping', VÄRNAMO: 'se-jonkoping', MULLSJÖ: 'se-jonkoping',
  SKILLINGARYD: 'se-jonkoping',

  // Kronobergs / Kalmar / Blekinge / Värmland / Örebro / Gotland
  VÄXJÖ: 'se-kronoberg',
  KALMAR: 'se-kalmar',
  KARLSKRONA: 'se-blekinge',
  KARLSTAD: 'se-varmland',
  ÖREBRO: 'se-orebro',
  VISBY: 'se-gotland',

  // Gävleborgs län
  GÄVLE: 'se-gavleborg', SANDVIKEN: 'se-gavleborg', HUDIKSVALL: 'se-gavleborg',
  DELSBO: 'se-gavleborg',

  // Dalarnas län
  FALUN: 'se-dalarna', BORLÄNGE: 'se-dalarna',

  // Västernorrlands län
  SUNDSVALL: 'se-vasternorrland', ÖRNSKÖLDSVIK: 'se-vasternorrland',

  // Jämtlands län
  ÖSTERSUND: 'se-jamtland', DUVED: 'se-jamtland',

  // Västerbottens län
  UMEÅ: 'se-vasterbotten', SKELLEFTEÅ: 'se-vasterbotten', GRANÖ: 'se-vasterbotten',

  // Norrbottens län
  LULEÅ: 'se-norrbotten', PITEÅ: 'se-norrbotten', KALIX: 'se-norrbotten',
  ÄLVSBYN: 'se-norrbotten', JUKKASJÄRVI: 'se-norrbotten',

  // ── Long-tail towns present in the data, and the area towns the map's
  //    postal-code fallback places leads in. ──
  NORRTÄLJE: 'se-stockholm', KIRUNA: 'se-norrbotten',
  VAXHOLM: 'se-stockholm', 'UPPLANDS-BRO': 'se-stockholm',
  'STOCKHOLM-ARLANDA': 'se-stockholm', 'NACKA STRAND': 'se-stockholm',
  DROTTNINGHOLM: 'se-stockholm', STOCKSUND: 'se-stockholm', VÄRMDÖ: 'se-stockholm',
  TUMBA: 'se-stockholm', FARSTA: 'se-stockholm', BANDHAGEN: 'se-stockholm',
  ÖSTERHANINGE: 'se-stockholm', SKÄRHOLMEN: 'se-stockholm',
  SVENLJUNGA: 'se-vastra-gotaland', ASKIM: 'se-vastra-gotaland',
  HINDÅS: 'se-vastra-gotaland', HYSSNA: 'se-vastra-gotaland',
  SKARA: 'se-vastra-gotaland', ULRICEHAMN: 'se-vastra-gotaland',
  FALKÖPING: 'se-vastra-gotaland', VARA: 'se-vastra-gotaland',
  KINNA: 'se-vastra-gotaland', ANGERED: 'se-vastra-gotaland',
  ÄLVÄNGEN: 'se-vastra-gotaland', 'HISINGS BACKA': 'se-vastra-gotaland',
  ARLÖV: 'se-skane', SIMRISHAMN: 'se-skane', ÅHUS: 'se-skane',
  LIMHAMN: 'se-skane', HÖGANÄS: 'se-skane', LÖDDEKÖPINGE: 'se-skane',
  FINJA: 'se-skane',
  OSKARSHAMN: 'se-kalmar', VÄSTERVIK: 'se-kalmar', BORGHOLM: 'se-kalmar',
  NYBRO: 'se-kalmar',
  VINTROSA: 'se-orebro', KUMLA: 'se-orebro', SKÖLLERSTA: 'se-orebro',
  KARLSHAMN: 'se-blekinge', RONNEBY: 'se-blekinge', VILSHULT: 'se-blekinge',
  HEDEMORA: 'se-dalarna', LEKSAND: 'se-dalarna', RÄTTVIK: 'se-dalarna',
  LÖVÅNGER: 'se-vasterbotten',
  ÅMOTFORS: 'se-varmland',
  DOCKSTA: 'se-vasternorrland', HÄRNÖSAND: 'se-vasternorrland',
  SLÖINGE: 'se-halland', VEDDIGE: 'se-halland',
  VADSTENA: 'se-ostergotland',
  VINGÅKER: 'se-sodermanland', STRÄNGNÄS: 'se-sodermanland',
  KATRINEHOLM: 'se-sodermanland',
  HABO: 'se-jonkoping', HUSKVARNA: 'se-jonkoping', GNOSJÖ: 'se-jonkoping',
  VETLANDA: 'se-jonkoping',
  MARKARYD: 'se-kronoberg',
  ÖSTERVÅLA: 'se-uppsala',
}

function lanRegion(key: string): Region {
  const m = LAN[key]
  return m ? { key, label: m.label, order: m.order } : UNKNOWN_REGION
}

// Postal-code prefixes / district suffixes addresses append to the city name:
// "SE-111 53 STOCKHOLM", "170 67 Solna", "København K".
const DISTRICT_SUFFIX = / (?:C|J|K|M|N|S|V|Ø|NV|SV|SØ|ØST)$/
const POSTAL_PREFIX = /^(?:[A-Z]{2}-)?\d{2,5}(?: ?\d{2})? +/

/** Lookup key for a raw city name: trimmed, uppercased, postal prefix off.
 *  For the län and coordinate tables; normalizeCity below is the display form. */
export function cityLookupKey(city: string): string {
  return city.trim().toUpperCase().replace(POSTAL_PREFIX, '')
}

/** The city name without a trailing district ("KØBENHAVN K" → "KØBENHAVN"). */
export function withoutDistrict(norm: string): string {
  return norm.replace(DISTRICT_SUFFIX, '')
}

/** Län for a raw (any-casing, possibly prefixed) Swedish city name. */
function lanForCity(city: string): Region {
  const norm = cityLookupKey(city)
  if (!norm) return UNKNOWN_REGION
  // leadgen marks companies with no Swedish locality as "Utlandet" (abroad).
  if (norm === 'UTLANDET') return OTHER_REGION
  const key = CITY_LAN[norm] ?? CITY_LAN[withoutDistrict(norm)]
  return key ? lanRegion(key) : UNKNOWN_REGION
}

/** Group a lead by region. Swedish leads → län (from the city name); other
 *  countries keep coarse coordinate buckets for the all-country map. */
export function regionFor(
  country: string,
  lat?: number | null,
  lng?: number | null,
  cityNorm?: string | null,
): Region {
  const c = (country || 'SE').toUpperCase()
  if (c === 'SE') return cityNorm ? lanForCity(cityNorm) : UNKNOWN_REGION
  if (lat == null || lng == null) return UNKNOWN_REGION
  // Non-SE coordinate regions sort below all 21 SE län (orders 0–20) so the
  // all-country map lists Swedish län first, foreign regions after.
  const r = foreignRegion(c, lat, lng)
  return { ...r, order: r.order + 50 }
}

function foreignRegion(c: string, lat: number, lng: number): Region {
  switch (c) {
    case 'NO':
      if (lat >= 65) return { key: 'no-north', label: 'North', order: 0 }
      if (lat >= 61.5) return { key: 'no-central', label: 'Central', order: 1 }
      if (lng < 6.5) return { key: 'no-west', label: 'West', order: 2 }
      if (lat < 58.8 && lng < 9.5) return { key: 'no-south', label: 'South', order: 4 }
      return { key: 'no-east', label: 'East', order: 3 }
    case 'FI':
      if (lat >= 64) return { key: 'fi-north', label: 'North', order: 0 }
      if (lat >= 61.4) return { key: 'fi-central', label: 'Central', order: 1 }
      return { key: 'fi-south', label: 'South', order: 2 }
    case 'DK':
      if (lng >= 10.9) return { key: 'dk-zealand', label: 'Zealand', order: 0 }
      if (lat < 55.65 && lng >= 9.7) return { key: 'dk-funen', label: 'Funen', order: 1 }
      return { key: 'dk-jutland', label: 'Jutland', order: 2 }
    default:
      return OTHER_REGION
  }
}

// Filter-dropdown order for the table's Län column: SE län north→south, then
// the catch-all buckets.
export const SE_REGION_ORDER = [
  ...Object.values(LAN).sort((a, b) => a.order - b.order).map(m => m.label),
  OTHER_REGION.label,
  UNKNOWN_REGION.label,
]

const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' })

/** An upper-case ISO country code ("SE", "NO"). Upper case and a real code
 *  only, so a two-letter town like "Ed" or "ED" still reads as a city. */
function isCountryCode(s: string): boolean {
  return /^[A-Z]{2}$/.test(s) && REGION_NAMES.of(s) !== undefined
}

function parseLocation(location?: string | null): { city: string; country: string } {
  const raw = (location || '').trim()
  // A country code on its own ("SE") names no city.
  if (isCountryCode(raw)) return { city: '', country: raw }
  const m = raw.match(/^(.*?)(?:,\s*([A-Za-z]{2}))?$/)
  const city = (m?.[1] || '').trim()
  const country = (m?.[2] || '').toUpperCase()
  // Nor does a country code in the city's place: "SE, SE" or "se, SE" is a
  // company stored with its country as the city.
  if (isCountryCode(city) || (country && city.toUpperCase() === country)) {
    return { city: '', country }
  }
  return { city, country }
}

/** Shown where a lead has no city, and the Ort filter's value for it. */
export const UNKNOWN_CITY = 'Okänd ort'

/**
 * A city name as it should read: "LULEÅ", "luleå" and "Luleå" all become
 * "Luleå", each part of "UPPLANDS-BRO" or "VÄSTRA FRÖLUNDA" capitalised, and a
 * postal-code prefix dropped. Empty when the input is empty.
 */
export function normalizeCity(raw?: string | null): string {
  return (raw || '').trim().replace(POSTAL_PREFIX, '')
    .toLocaleLowerCase('sv-SE')
    .split(/([\s-]+)/)
    .map(part => part.charAt(0).toLocaleUpperCase('sv-SE') + part.slice(1))
    .join('')
}

/** Normalised city of a `Lead.location` string ("LULEÅ, SE" → "Luleå"), or
 *  empty when it has none. */
export function cityOf(location?: string | null): string {
  return normalizeCity(parseLocation(location).city)
}

/** City part of a `Lead.location` string, or UNKNOWN_CITY. Used as the Ort
 *  column's value, so the filter, sort and cell agree on one spelling. */
export function cityOfLocation(location?: string | null): string {
  return cityOf(location) || UNKNOWN_CITY
}

/** "LULEÅ, SE" → "Luleå, SE"; empty when there is no city to show. */
export function locationLabel(location?: string | null): string {
  const { city, country } = parseLocation(location)
  const name = normalizeCity(city)
  if (!name) return ''
  return country ? `${name}, ${country}` : name
}

// Location strings repeat heavily across leads — cache the resolved label.
const locationRegionCache = new Map<string, string>()

/** Län label for a `Lead.location` string ("City, CC"). Non-SE → "Utlandet". */
export function regionOfLocation(location?: string | null): string {
  const loc = (location || '').trim()
  if (!loc) return UNKNOWN_REGION.label
  const cached = locationRegionCache.get(loc)
  if (cached) return cached
  const { city, country } = parseLocation(loc)
  const label = country && country !== 'SE' ? OTHER_REGION.label : lanForCity(city).label
  locationRegionCache.set(loc, label)
  return label
}
